import { useCallback, useState } from "react";
import { DocumentData } from "../../components/documents/DocumentCollector/types";
import useDocumentDelete from "./useDocumentDelete";
import useDocumentUpload from "./useDocumentUpload";
import useUpdateDocumentMetadata from "./useUpdateDocumentMetadata";
import useUpdateDocumentsCompleted from "./useUpdateDocumentsCompleted";

/**
 * Type definition for document form values
 */
export type DocumentFormValues = {
  documents: DocumentData[];
  skipDocumentUpload?: boolean;
};

/**
 * Orchestrates document management operations (upload, delete, metadata update)
 * by coordinating specialized hooks for each operation type.
 *
 * @param caseId - Case ID for document operations
 * @param profileId - Profile ID for document association
 * @returns { processDocuments, loading } - Function for processing documents and loading state
 */
export default function useDocumentProcessor(
  caseId: number | undefined,
  profileId: number | undefined,
) {
  const [isProcessing, setIsProcessing] = useState(false);

  // Use specialized hooks for document operations
  const { updateDocumentsCompleted, loading: updateDocumentsCompletedLoading } =
    useUpdateDocumentsCompleted(caseId, profileId);
  const { deleteDocuments, loading: deleteLoading } = useDocumentDelete(caseId);
  const { uploadDocuments, loading: uploadLoading } = useDocumentUpload(caseId);
  const { updateDocumentMetadata, loading: updateLoading } = useUpdateDocumentMetadata(
    caseId,
    profileId,
  );

  /**
   * Main orchestration function that:
   * 1. Identifies files to delete and upload
   * 2. Deletes replaced documents
   * 3. Uploads new documents
   * 4. Updates document metadata
   *
   * @param newSelectedFiles - Form values with current document selections
   * @param initialFiles - Original documents for comparison
   * @returns Promise resolving to updated documents
   */
  const processDocuments = useCallback(
    async (newFiles: DocumentData[], deleteDocIds: number[]) => {
      setIsProcessing(true);
      try {
        // First delete documents that have been replaced
        await deleteDocuments(deleteDocIds);

        // Then upload and register new documents
        const updatedDocuments = await uploadDocuments(newFiles);

        // Finally update document metadata
        await updateDocumentMetadata(updatedDocuments);

        await updateDocumentsCompleted();

        return updatedDocuments;
      } finally {
        setIsProcessing(false);
      }
    },
    [deleteDocuments, uploadDocuments, updateDocumentMetadata],
  );

  return {
    processDocuments,
    loading:
      isProcessing ||
      deleteLoading ||
      uploadLoading ||
      updateLoading ||
      updateDocumentsCompletedLoading,
  };
}
